import { useState, useEffect } from "react";

import { makeGetApiCall, makePostApiCall } from "../../../shared/makeApiCall";
import ImportMediaCsvDropzone from "../../../components/importMedia/ImportMediaCsvDropzone";
import ModelErrorComponent from "../../../components/modals/ModelErrorComponent";
import DeleteConfirmationForm from "../../../components/forms/DeleteConfirmationForm";
import { SnackbarData, SnackbarType } from "../../../components/Snackbar";
import { generateUniqueNumber } from "../../../shared/utility";
import FilterSingleSelectDropdown from "../../../components/filterDropdown/FilterSingleSelectDropdown";

export function ProductsViewModel(logout, addSnackbar) {
  const [filters, setFilters] = useState({
    product_category: { value: -1, label: "All categories" },
    product_subcategory: { value: -1, label: "All sub categories" },
  });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);

  const getProducts = async function (search, page, filter) {
    const payload = {
      page: page,
      search: search,
      filter: filter,
    };
    let callResult = await makePostApiCall(
      `products?search=${search}&page=${page}`,
      payload,
    );
    if (callResult.success) {
      const response = callResult.data;
      if (page > 1) {
        setProducts([...products, ...response.data]);
      } else {
        setTotalProducts(response.total);
        setProducts(response.data);
        setLoading(false);
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const uploadProductsCsv = async function (uploadedFile) {
    const { name, value } = uploadedFile.target;
    const payload = { [name]: value };
    let callResult = await makePostApiCall(
      "uploadProductsCsv",
      payload,
      "multipart/form-data",
    );

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      getProducts("", 0);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const openImportProduct = () => {
    setEmptyModelOpen({
      childComponent: (
        <ImportMediaCsvDropzone
          fileName="productsCsv"
          actionCall={uploadProductsCsv}
          setOpen={setEmptyModelOpen}
          title="Products"
        />
      ),
      open: true,
    });
  };

  const showErrorModal = (subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title="Error"
        />
      ),
      open: true,
    });
  };

  const deleteProductById = async function (productId) {
    let callResult = await makePostApiCall(`deleteProduct/${productId}`);

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );

      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      getProducts("", 0);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch product");
    }
    return callResult;
  };

  const getProductCategories = async function () {
    let callResult = await makeGetApiCall(`categoriesForDropdown`);
    if (callResult.success) {
      setCategoryOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getProductSubCategories = async function (categoryId) {
    let callResult = await makeGetApiCall(
      `subcategoriesForDropdown/${categoryId}`,
    );
    if (callResult.success) {
      setSubcategoryOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  useEffect(() => {
    if (filters.product_category.value === -1) {
      setSubcategoryOptions([]);
    }
  }, [filters.product_category.value]);

  const filterComponents = [
    <FilterSingleSelectDropdown
      header="Category"
      onChange={(value) => {
       
        setFilters({
          ...filters,
          product_category: value,
          product_subcategory: { value: -1, label: "All sub categories" },
        });

        getProductSubCategories(value.value);
      }}
      options={categoryOptions}
      resetFilter={() =>
        setFilters({
          ...filters,
          product_category: { value: -1, label: "All categories" },
        })
      }
      valueName="product_category"
      value={filters.product_category}
    />,
    <FilterSingleSelectDropdown
      header="Sub Category"
      onChange={(value) =>
        setFilters({
          ...filters,
          product_subcategory: value,
        })
      }
      options={subcategoryOptions}
      resetFilter={() =>
        setFilters({
          ...filters,
          product_subcategory: { value: -1, label: "All sub categories" },
        })
      }
      valueName="product_subcategory"
      value={filters.product_subcategory}
      disabled={filters.product_category.value === -1}
    />,
  ];

  const resetFilters = () => {
    const filterReset = {
      product_category: { value: -1, label: "All categories" },
      product_subcategory: { value: -1, label: "All sub categories" },
    };
    setFilters(filterReset);

    setShowFilters(false);
    getProducts(null, 1, filterReset);
  };

  const askDeleteProduct = function (product) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteProductById(product.id)}
          message={`Are you sure you want to delete  ${product.name}?`}
          setOpen={setEmptyModelOpen}
          hideReason={true}
        />
      ),
      open: true,
    });
  };

  return {
    getProducts,
    loading,
    products,
    setSlideOver,
    slideOver,
    openImportProduct,
    setEmptyModelOpen,
    emptyModal,
    askDeleteProduct,
    getProductCategories,
    showFilters,
    setShowFilters,
    filters,
    filterComponents,
    resetFilters,
    totalProducts,
  };
}
